import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { site_url } from './../config.js';
import Sidebar from './../Sidebar';
import Footer from './../Footer';
import { AuthContext } from './../AuthContext';
import Swal from 'sweetalert2';

function DenominationListCal() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const denominationId = params.get('id');
  const { user } = useContext(AuthContext);

  const [note500, setNote500] = useState(0);
  const [note200, setNote200] = useState(0);
  const [note100, setNote100] = useState(0);
  const [note50, setNote50] = useState(0);
  const [note20, setNote20] = useState(0);
  const [note10, setNote10] = useState(0);
  const [coins, setCoins] = useState(0);
  const [requiredPayment, setRequiredPayment] = useState(0);
  const [collectionDate, setCollectionDate] = useState('');
  const [routeName, setRouteName] = useState('');
  const [routeId, setRouteId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const totalCash = useMemo(() => (
    (Number(note500) || 0) * 500 +
    (Number(note200) || 0) * 200 +
    (Number(note100) || 0) * 100 +
    (Number(note50) || 0) * 50 +
    (Number(note20) || 0) * 20 +
    (Number(note10) || 0) * 10 +
    (Number(coins) || 0)
  ), [note500, note200, note100, note50, note20, note10, coins]);

  const balanceReturn = useMemo(() => {
    const req = Number(requiredPayment) || 0;
    return totalCash - req;
  }, [totalCash, requiredPayment]);

  // Helper function to handle integer-only input
  const handleIntegerInput = (value, setter) => {
    const intValue = value === '' ? '' : value.toString().replace(/[^0-9]/g, '');
    setter(intValue === '' ? '' : intValue);
  };

  useEffect(() => {
    const fetchDenomination = async () => {
      if (!denominationId) {
        Swal.fire({
          title: 'Error!',
          text: 'Denomination ID is missing',
          icon: 'error',
          confirmButtonText: 'OK',
        }).then(() => {
          navigate('/Denominationlist');
        });
        return;
      }

      setLoading(true);
      try {
        const res = await axios.get(`${site_url}/edit_denomination/${denominationId}`);
        if (res.data && res.data.status === 'success' && res.data.denomination) {
          const denom = res.data.denomination;
          setNote500(denom['500amount'] || 0);
          setNote200(denom['200amount'] || 0);
          setNote100(denom['100amount'] || 0);
          setNote50(denom['50amount'] || 0);
          setNote20(denom['20amount'] || 0);
          setNote10(denom['10amount'] || 0);
          setCoins(denom['coinsamount'] || 0);
          setRequiredPayment(denom.totalrouteamount || denom.totalcollectionamount || 0);
          setCollectionDate(denom.collectiondate || new Date().toISOString().split('T')[0]);
          setRouteId(denom.routeid || '');
          setRouteName(denom.route_name || '');
        } else {
          throw new Error('Failed to fetch denomination data');
        }
      } catch (error) {
        console.error('Error fetching denomination:', error);
        Swal.fire({
          title: 'Error!',
          text: error.response?.data?.message || error.message || 'Failed to load denomination',
          icon: 'error',
          confirmButtonText: 'OK',
        }).then(() => {
          navigate('/Denominationlist');
        });
      } finally {
        setLoading(false);
      }
    };

    if (user && user.id && denominationId) {
      fetchDenomination();
    }
  }, [denominationId, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user || !user.id) {
      Swal.fire({
        title: 'Error!',
        text: 'User information is missing',
        icon: 'error',
        confirmButtonText: 'OK',
      });
      return;
    }

    // if (!routeId) {
    //   Swal.fire({
    //     title: 'Error!',
    //     text: 'Route ID is missing',
    //     icon: 'error',
    //     confirmButtonText: 'OK',
    //   });
    //   return;
    // }

    setIsSubmitting(true);

    try {
      const denominationData = {
        id: denominationId,
        w_id: user.id,
        routeid: routeId,
        '500amount': note500.toString(),
        '200amount': note200.toString(),
        '100amount': note100.toString(),
        '50amount': note50.toString(),
        '20amount': note20.toString(),
        '10amount': note10.toString(),
        'coinsamount': coins.toString(),
        totalcollectionamount: totalCash.toString(),
        totalrouteamount: requiredPayment || 0,
        collectiondate: collectionDate
      };

      const response = await axios.post(site_url + '/update_denomination', denominationData);

      if (response.data && response.data.status === 'success') {
        Swal.fire({
          title: 'Success!',
          text: response.data.message || 'Denomination updated successfully',
          icon: 'success',
          confirmButtonText: 'OK',
        }).then(() => {
          navigate('/Denominationlist');
        });
      } else {
        throw new Error(response.data?.message || 'Failed to update denomination');
      }
    } catch (error) {
      console.error('Error updating denomination:', error);
      Swal.fire({
        title: 'Error!',
        text: error.response?.data?.message || error.message || 'Failed to update denomination',
        icon: 'error',
        confirmButtonText: 'OK',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetAll = () => {
    setNote500(0);
    setNote200(0);
    setNote100(0);
    setNote50(0);
    setNote20(0);
    setNote10(0);
    setCoins(0);
  };

  if (loading) {
    return (
      <div className="container p-3">
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading denomination data...</p>
        </div>
        <Sidebar />
      </div>
    );
  }

  return (
    <div className="container p-3">
      <div className="d-flex align-items-center justify-content-between mt-5">
        <div>
          <h3 className="mb-0">Edit Denomination</h3>
          {routeName && <p className="text-muted mb-0">Route: {routeName}</p>}
        </div>
        <div className="d-flex gap-2">
          <button 
            className="btn btn-outline-secondary" 
            onClick={() => navigate('/Denominationlist')}
          >
            Back to List
          </button>
          <button className="btn btn-outline-warning" onClick={resetAll} disabled={isSubmitting}>
            Reset
          </button>
          <button 
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Updating...
              </>
            ) : (
              'Update Denomination'
            )}
          </button>
        </div>
      </div>

      <div className="row mt-3 g-3">
        <div className="col-12 col-md-4">
          <div className="card shadow-sm">
            <div className="card-body">
              <label className="form-label">Collection Date</label>
              <input
                type="date"
                className="form-control mb-3"
                value={collectionDate}
                onChange={(e) => setCollectionDate(e.target.value)}
                required
              />
              <label className="form-label">Total Collection Amount</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="form-control"
                value={requiredPayment}
                onChange={(e) => setRequiredPayment(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="col-12 col-md-8">
          <div className="card shadow-sm">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-bordered table-sm mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{width:'30%'}}>Denomination</th>
                      <th style={{width:'35%'}}>Count</th>
                      <th style={{width:'35%'}}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><b>₹ 500</b></td>
                      <td>
                        <input 
                          type="number" 
                          min="0" 
                          step="1"
                          className="form-control" 
                          value={note500} 
                          onChange={(e) => handleIntegerInput(e.target.value, setNote500)}
                        />
                      </td>
                      <td>₹ {((Number(note500)||0)*500).toLocaleString('en-IN')}</td>
                    </tr>
                    <tr>
                      <td><b>₹ 200</b></td>
                      <td>
                        <input 
                          type="number" 
                          min="0" 
                          step="1"
                          className="form-control" 
                          value={note200} 
                          onChange={(e) => handleIntegerInput(e.target.value, setNote200)}
                        />
                      </td>
                      <td>₹ {((Number(note200)||0)*200).toLocaleString('en-IN')}</td>
                    </tr>
                    <tr>
                      <td><b>₹ 100</b></td>
                      <td>
                        <input 
                          type="number" 
                          min="0" 
                          step="1"
                          className="form-control" 
                          value={note100} 
                          onChange={(e) => handleIntegerInput(e.target.value, setNote100)}
                        />
                      </td>
                      <td>₹ {((Number(note100)||0)*100).toLocaleString('en-IN')}</td>
                    </tr>
                    <tr>
                      <td><b>₹ 50</b></td>
                      <td>
                        <input 
                          type="number" 
                          min="0" 
                          step="1"
                          className="form-control" 
                          value={note50} 
                          onChange={(e) => handleIntegerInput(e.target.value, setNote50)}
                        />
                      </td>
                      <td>₹ {((Number(note50)||0)*50).toLocaleString('en-IN')}</td>
                    </tr>
                    <tr>
                      <td><b>₹ 20</b></td>
                      <td>
                        <input 
                          type="number" 
                          min="0" 
                          step="1"
                          className="form-control" 
                          value={note20} 
                          onChange={(e) => handleIntegerInput(e.target.value, setNote20)}
                        />
                      </td>
                      <td>₹ {((Number(note20)||0)*20).toLocaleString('en-IN')}</td>
                    </tr>
                    <tr>
                      <td><b>₹ 10</b></td>
                      <td>
                        <input 
                          type="number" 
                          min="0" 
                          step="1"
                          className="form-control" 
                          value={note10} 
                          onChange={(e) => handleIntegerInput(e.target.value, setNote10)}
                        />
                      </td>
                      <td>₹ {((Number(note10)||0)*10).toLocaleString('en-IN')}</td>
                    </tr>
                    <tr>
                      <td><b>₹ Coins</b></td>
                      <td>
                        <input 
                          type="number" 
                          min="0" 
                          step="1"
                          className="form-control" 
                          value={coins} 
                          onChange={(e) => handleIntegerInput(e.target.value, setCoins)}
                        />
                      </td>
                      <td>₹ {((Number(coins)||0)*1).toLocaleString('en-IN')}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row mt-3 g-3">
        <div className="col-12 col-md-4">
          <div className="card border-success">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted">Total Cash</span>
                <h4 className="text-success mb-0">₹ {totalCash.toLocaleString('en-IN')}</h4>
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-4">
          <div className="card border-primary">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted">Collection Amount</span>
                <h4 className="text-primary mb-0">₹ {(Number(requiredPayment)||0).toLocaleString('en-IN')}</h4>
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-4 mb-5" style={{marginBottom:'100px'}}>
          <div className="card border-warning">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted">Balance</span>
                <h4 className={`mb-0 ${balanceReturn >= 0 ? 'text-warning' : 'text-danger'}`}>
                  ₹ {balanceReturn.toLocaleString('en-IN')}
                </h4>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
      <Sidebar />
    </div>
  );
}

export default DenominationListCal;

